import { useEffect, useState } from "react";
import { Fade } from "@mui/material";
import { toast } from "react-hot-toast";
import { RequestCondominiosPaginados } from "types/request/RequestCondominiosPaginados";
import { useGetRegistrosTabelaCondominioGroupPay } from "hooks/condominio/useGetRegistrosTabelaCondominioGroupPay";
import { usePostAtivarCondominioGroupPay } from "hooks/groupPay/usePostAtivarCondominioGroupPay";
import { usePostInativarCondominioGroupPay } from "hooks/groupPay/usePostInativarCondominioGroupPay";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import useStyles from "./styles/TelaConfiguracaoCondominiosIntegradosGroupPayStyles";
import TabelaConfigurarCondominios from "./TabelaConfigurarCondominios";
import ModalConfiguracaoBuscaExterna from "./ModalConfiguracaoBuscaExterna";
import { Condominio } from "./ModalReplicarAcesso";
import StandardModal from "shared/components/standardModal/StandardModal";

const TelaConfiguracaoCondominiosIntegradosGroupPay: React.FC = () => {
  const classes = useStyles();
  const [searchCondominio, setSearchCondominio] = useState<string>("");
  const [sortString, setSortString] = useState<string>("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [mostrarConteudo, setMostrarConteudo] = useState<boolean>(false);
  const [showModalConfiguracaoBuscaExterna, setShowModalConfiguracaoBuscaExterna] = useState<boolean>(false);
  const [condominioSelecionado, setCondominioSelecionado] = useState<Condominio | null>(null);
  const [showModalConfirmacao, setShowModalConfirmacao] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState({
    topBar: "",
    titulo: "",
    conteudoPrimario: "",
    footerPrimaryLabel: "",
    footerSecondaryLabel: "",
    onFooterPrimaryClick: () => {},
    onFooterSecondaryClick: () => {},
  });

  const params = {
    page: paginaAtual - 1,
    sort: sortString,
    nomeCondominio: searchCondominio,
    size: 99999,
  } as RequestCondominiosPaginados;

  const { data: condominiosPaginados } = useGetRegistrosTabelaCondominioGroupPay(params);
  const { mutateAsync: postAtivarCondominio, isLoading: isAtivandoCondominio } = usePostAtivarCondominioGroupPay();
  const { mutateAsync: postInativarCondominio, isLoading: isInativandoCondominio } =
    usePostInativarCondominioGroupPay();

  const ativarInativarGroupPay = (isGroupPayAtivo: boolean, condominio: Condominio) => {
    const handleInativar = () => {
      postInativarCondominio({ ids: [condominio.condominioId] })
        .then(() => {
          setShowModalConfirmacao(false);
        })
        .catch((error) => {
          possuiMensagemDeErro(error)
            ? toast.error(getMensagemDeErroBackend(error))
            : toast.error("Erro ao inativar o condomínio no Group Pay");
        });
    };

    const handleAtivarEConfigurar = () => {
      postAtivarCondominio({ ids: [condominio.condominioId] })
        .then(() => {
          toast.success("Condomínio ativado no Group Pay com sucesso.");
          openModal(condominio);
          setShowModalConfirmacao(false);
        })
        .catch((error) => {
          possuiMensagemDeErro(error)
            ? toast.error(getMensagemDeErroBackend(error))
            : toast.error("Erro ao ativar o condomínio no Group Pay");
        });
    };

    const handleSomenteAtivar = () => {
      postAtivarCondominio({ ids: [condominio.condominioId] })
        .then(() => {
          toast.success("Condomínio ativado no Group Pay com sucesso.");
          setShowModalConfirmacao(false);
        })
        .catch((error) => {
          possuiMensagemDeErro(error)
            ? toast.error(getMensagemDeErroBackend(error))
            : toast.error("Erro ao ativar o condomínio no Group Pay");
        });
    };

    if (!isGroupPayAtivo) {
      setModalContent({
        topBar: "Ativar Group Pay",
        titulo: "Tem certeza que deseja ativar o Group Pay para o condomínio?",
        conteudoPrimario: "Recomendamos que faça a configuração de busca por nota fiscal após a ativação.",
        footerPrimaryLabel: "Ativar e configurar",
        onFooterPrimaryClick: handleAtivarEConfigurar,
        footerSecondaryLabel: "Somente ativar",
        onFooterSecondaryClick: handleSomenteAtivar,
      });
    } else {
      setModalContent({
        topBar: "Desativar Group Pay",
        titulo: "Tem certeza que deseja desativar o Group Pay para o condomínio?",
        conteudoPrimario: `Ao desabilitar o Group Pay para o condomínio ${condominio.nomeFantasia}, todas as buscas de notas fiscais deverão ser feitas manualmente a partir da data atual.`,
        footerPrimaryLabel: "Desabilitar condomínio",
        onFooterPrimaryClick: handleInativar,
        footerSecondaryLabel: "Cancelar",
        onFooterSecondaryClick: () => setShowModalConfirmacao(false),
      });
    }

    setShowModalConfirmacao(true);
  };

  useEffect(() => {
    setMostrarConteudo(true);
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [searchCondominio]);

  const openModal = (condominio: Condominio) => {
    setShowModalConfiguracaoBuscaExterna(true);
    setCondominioSelecionado(condominio);
  };

  return (
    <>
      <Fade in={mostrarConteudo} timeout={700}>
        <div>
          <div>
            <h2 className={classes.title}>Ativação do Group Pay</h2>
            <p className={classes.subtitle}>Ative abaixo os condomínios nos quais você deseja utilizar o Group Pay</p>
          </div>
          <TabelaConfigurarCondominios
            condominios={condominiosPaginados?.content ?? []}
            searchCondominio={searchCondominio}
            setSearchCondominio={setSearchCondominio}
            renderUsarGroupPay={true}
            onChangeUsarGroupPay={ativarInativarGroupPay}
            openModal={openModal}
            setSortString={setSortString}
          />
        </div>
      </Fade>
      <ModalConfiguracaoBuscaExterna
        showModal={showModalConfiguracaoBuscaExterna}
        condominioId={condominioSelecionado?.condominioId ?? null}
        condominio={condominioSelecionado}
        closeModal={() => setShowModalConfiguracaoBuscaExterna(false)}
        setShowModalConfiguracaoBuscaExterna={setShowModalConfiguracaoBuscaExterna}
      />

      <StandardModal
        showModal={showModalConfirmacao}
        onCloseModal={() => setShowModalConfirmacao(false)}
        {...modalContent}
        footerPrimaryVariant="primary"
        footerSecondaryVariant="outline-dark"
        footerSecondarySize="lg"
        footerSecondaryStyle={{ minWidth: "150px", padding: "0.5rem 1.5rem" }}
        footerPrimaryDisabled={isAtivandoCondominio || isInativandoCondominio}
        footerSecondaryDisabled={isAtivandoCondominio || isInativandoCondominio}
        footerContainerClassName={classes.modalFooter}
        size="sm"
        fecharModalAoClicarFora={true}
        centered={true}
        usarSize={false}
      />
    </>
  );
};

export default TelaConfiguracaoCondominiosIntegradosGroupPay;
